// Comprobantes de pago (fotos, captures y PDF) guardados en Supabase Storage, en un bucket privado.
import { supabase } from '../supabaseClient';
import { q } from './vinculos';

export const BUCKET = 'comprobantes';

export const RUBROS = [
  { value: 'hogar', label: '🏠 Hogar' },
  { value: 'impuestos', label: '🧾 Impuestos / SII' },
  { value: 'empresa', label: '🏢 Empresa / Tienda' },
  { value: 'personal', label: '👤 Personal' },
];

export const nombreRubro = (value) => RUBROS.find(r => r.value === value)?.label || 'Sin rubro';

export const ACEPTA_ARCHIVOS = 'image/*,application/pdf';

const PALABRAS_RUBRO = [
  ['impuestos', ['sii', 'f29', 'iva', 'patente', 'declaracion', 'declaración', 'tesoreria', 'tesorería', 'impuesto', 'multa', 'renta anual']],
  ['hogar', ['arriendo', 'garantia', 'garantía', 'gasto comun', 'gasto común', 'gacom', 'gc ', 'enel', 'luz', 'aguas', 'agua', 'entel', 'internet', 'gas ', 'metrogas']],
  ['empresa', ['multservice', 'flex', 'mercado libre', 'envio', 'envío', 'proveedor', 'mercaderia', 'mercadería']],
];

// Rubro probable a partir del concepto o la ruta del archivo.
export function adivinarRubro(texto) {
  const t = ` ${(texto || '').toLowerCase()} `;
  const hit = PALABRAS_RUBRO.find(([, palabras]) => palabras.some(p => t.includes(p)));
  return hit ? hit[0] : '';
}

const MESES_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const pad = (n) => String(n).padStart(2, '0');

function mesDe(texto) {
  const t = texto.toLowerCase();
  const i = MESES_ES.findIndex(m => t.includes(m));
  return i >= 0 ? i + 1 : null;
}
const anoDe = (texto) => {
  const m = texto.match(/\b(20\d{2})\b/);
  return m ? Number(m[1]) : null;
};

// Lee concepto, monto y fecha del nombre del archivo, ej:
//   "Enel Abril 2026 $33.496 11-05.jpg"  → Enel Abril 2026 · 33496 · 2026-05-11
//   "GC JUNIO $98.779 29-7.jpeg" en la carpeta "JULIO 2026" → GC JUNIO · 98779 · 2026-07-29
// La carpeta (ej. "JULIO 2026") da el año y el mes cuando el nombre no los trae.
export function leerNombreArchivo(nombre, rutaRelativa = '') {
  const base = nombre.replace(/(\.(jpe?g|png|pdf|webp|heic|gif))+$/i, '').replace(/[_]+/g, ' ').trim();
  const carpeta = rutaRelativa.split('/').slice(0, -1).join(' ');

  // Monto: lo que viene después de "$", o un número con puntos de miles (ej. 298.000)
  let monto = null;
  const conPeso = base.match(/\$\s*([\d.]+)/);
  const conMiles = base.match(/\b(\d{1,3}(?:\.\d{3})+)\b/);
  const textoMonto = conPeso?.[1] || conMiles?.[1];
  if (textoMonto) monto = Number(textoMonto.replace(/\./g, '')) || null;

  // Fecha: "dd-mm" en el nombre; año del nombre o de la carpeta.
  const ano = anoDe(base) || anoDe(carpeta);
  const mesCarpeta = mesDe(carpeta);
  let fecha = null;
  const dma = base.match(/\b(\d{1,2})[-/](\d{1,2})[-/](20\d{2})\b/); // fecha completa: dd-mm-aaaa
  if (dma) {
    const [d, m, a] = [Number(dma[1]), Number(dma[2]), Number(dma[3])];
    if (d >= 1 && d <= 31 && m >= 1 && m <= 12) fecha = `${a}-${pad(m)}-${pad(d)}`;
  }
  const dm = base.replace(/\b20\d{2}\b/g, '').match(/\b(\d{1,2})[-/](\d{1,2})\b/);
  if (!fecha && dm && ano) {
    const d = Number(dm[1]);
    const m = Number(dm[2]);
    if (d >= 1 && d <= 31 && m >= 1 && m <= 12) fecha = `${ano}-${pad(m)}-${pad(d)}`;
  }
  if (!fecha && ano && (mesCarpeta || mesDe(base))) {
    fecha = `${ano}-${pad(mesCarpeta || mesDe(base))}-01`;
  }

  // Concepto: el nombre sin el monto ni la fecha corta
  let concepto = base
    .replace(/\b(pagad[oa]|pago el)\s+\d{1,2}[-/]\d{1,2}[-/]20\d{2}\b/gi, '')
    .replace(/\b\d{1,2}[-/]\d{1,2}[-/]20\d{2}\b/g, '')
    .replace(/\$\s*[\d.]+/g, '')
    .replace(/\b\d{1,3}(?:\.\d{3})+\b/g, '')
    .replace(/\b\d{1,2}[-/]\d{1,2}\b/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  if (!concepto) concepto = base;

  return { concepto, monto, fecha, rubro: adivinarRubro(`${base} ${carpeta}`) };
}

const nombreSeguro = (n) => n.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9._-]+/g, '_').slice(-80);

// Sube el archivo y crea el registro del comprobante.
export async function subirComprobante({ userId, file, fecha, monto, concepto, rubro, gastoFijoId = null, movimientoId = null, origen = 'app' }) {
  const carpetaMes = (fecha || '').slice(0, 7) || 'sin-fecha';
  const path = `${userId}/${carpetaMes}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${nombreSeguro(file.name)}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, { contentType: file.type || undefined, upsert: false });
  if (error) throw error;
  try {
    return await q(supabase.from('comprobantes').insert({
      user_id: userId,
      fecha: fecha || null,
      monto: monto === '' || monto == null ? null : Number(monto),
      concepto: concepto || file.name,
      rubro: rubro || null,
      gasto_fijo_id: gastoFijoId,
      movimiento_id: movimientoId,
      archivo_path: path,
      archivo_nombre: file.name,
      archivo_tamano: file.size,
      origen,
    }).select().single());
  } catch (e) {
    await supabase.storage.from(BUCKET).remove([path]); // no dejar archivos huérfanos
    throw e;
  }
}

// Abre el comprobante en una pestaña nueva (link temporal de 10 minutos).
export async function abrirComprobante(c) {
  const ventana = window.open('', '_blank'); // abrir antes del await para que el navegador no la bloquee
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(c.archivo_path, 600);
  if (error) { if (ventana) ventana.close(); throw error; }
  if (ventana) ventana.location.href = data.signedUrl;
  else window.location.href = data.signedUrl;
}

export async function borrarComprobante(c) {
  await q(supabase.from('comprobantes').delete().eq('id', c.id));
  await supabase.storage.from(BUCKET).remove([c.archivo_path]);
}

export async function comprobantesDeMovimientos(userId) {
  return q(supabase.from('comprobantes').select('*').eq('user_id', userId).not('movimiento_id', 'is', null));
}
